import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import { getUrlParam } from 'utils/common';
import dayjs from 'utils/dayjs';
import { getTimeStr } from 'utils/calendar';
import useTimezone from 'hooks/useTimezone';
import { SUPPORT_TIME_ZONE, ZOOM_AUTO_REFRESH_INTERVAL } from 'constants/common';
import KlarityHeader from 'components/KlarityHeader';
import klarityLogo from 'assets/logo/klarity.png';
import ProviderStore from 'store/Provider';
import CommonStore from 'store/Common';
import { Provider } from 'types/provider';
import ProviderZoomCallView from 'components/ProviderZoomCallView';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import { useGetConnectAccountInfo } from 'hooks/useGetConnectAccountInfo';
import { useBillingPolicy } from 'hooks/paymentSetup/useBillingPolicy';
import useProspectInfo from 'hooks/useProspectInfo';
import Prescriptions from 'components/Prescriptions';
import { Button, message, Modal, Popover, Spin, Switch } from 'antd';
import useProviderMeetingInfo from 'hooks/useProviderMeetingInfo';
import useProviderMeetingToken from 'hooks/useProviderMeetingToken';
import { getMeetingUuid, setMeetingUuid } from 'utils/sessionStorage';
import { syncScheduleTokenFromCookie } from 'utils/shareAccount';
import { EMeetingUserType, EZoomVideoViewActionType, TServerMeetingRoomUser } from 'types/zoom';
import IconLogo from 'assets/logo/iconLogo.png';
import { meetingApprove, meetingEnd } from 'api/zoom';
import useLocalTestZoomMeetingInfo from 'hooks/useLocalTestZoomMeetingInfo';
import useTreatmentAppointmentDetail from 'hooks/treatment/useTreatmentAppointmentDetail';
import SendMessageToPatientModal from 'components/SendMessageToPatientModal';
import useGetCommingAppointment from 'hooks/useGetCommingAppointment';
import ChatBox from 'components/ChatBox';
import { PATH } from 'constants/path';
import { EMessageType, TMessage, TMessageFrom } from 'types/inbox';
import { submitVideoTreatment } from 'api/treatment';
import { EBusinessType } from 'types/treatment';
import { TextVisitAttachment } from 'types/textVisit';
import useVideoAndAudioPermisson from 'hooks/useVideoAndAudioPermisson';
import PermissionNotAllowModal from 'components/PermissionNotAllowModal';
import useLoadBasicData from 'hooks/useLoadBasicData';
import ProviderSignature from 'components/ProviderSignature';

export enum ELayoutType {
    MAIN_LEFT = 'MAIN_LEFT',
    MAIN_RIGHT = 'MAIN_RIGHT',
    CENTER = 'CENTER',
    FLOAT_VIDEO = 'FLOAT_VIDEO',
}

const LOCAL_TEST = false;

const ratio = 9 / 16;

const MeetingPage = () => {
    const navigate = useNavigate();
    const [getProvider] = ProviderStore.useStore();
    const [getCommonStore] = CommonStore.useStore();
    // App 级白屏 loading 已结束（页面真正可见）。注意它远早于 Zoom join 成功，且 Zoom 可能根本 join 不上
    const appLoadingDone = !!getCommonStore('appLoadingDone');
    const provider: Provider = getProvider('data') as Provider;
    const [layout, setLayout] = useState<ELayoutType>(ELayoutType.MAIN_RIGHT);
    const [hidePrescriptions, setHidePrescriptions] = useState(false);
    const [hideTreatmentPlan, setHideTreatmentPlan] = useState(false);
    const [hideNotes, setHideNotes] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const admitedUserIds = React.useRef<number[]>([]);
    const [nextMeetingLeftMinutes, setNextMeetingLeftMinutes] = useState<number>(5);
    const [checked, allowVideo, allowAudio] = useVideoAndAudioPermisson();

    //360px
    const [hideClientInfo, setHideClientInfo] = useState(true);
    //380px
    const [hideMessage, setHideMessage] = useState(true);

    const [connectAccountInfo, loadingConnectAccountInfo] = useGetConnectAccountInfo();
    const [billingPolicy, loadingBillingPolicy] = useBillingPolicy(false, provider?.id);
    const [testInfo] = useLocalTestZoomMeetingInfo();
    const [patientId, setPatientId] = useState<number | undefined>();
    const patientIdRef = useRef<number | undefined>();
    const [targetClientDetail, loadingTargetClientDetail, runTargetClientDetail] = useProspectInfo(patientId);

    const [sessionKey, setSessionKey] = useState<string | undefined>(); //from url
    const [refreshTime, setRefreshTime] = useState<number>(3000);
    const [meetingInfo, loadingMeetingInfo, fetchMeetingInfo] = useProviderMeetingInfo(sessionKey, true, refreshTime);
    const [zoomToken, loadingToken, fetchToken] = useProviderMeetingToken(sessionKey);
    const [pendingJoinUser, setPendingJoinUser] = useState<TServerMeetingRoomUser | undefined>();

    const [, myTimezone, isDifferentTimezoneWithBrowser] = useTimezone(true);
    const [admitLoading, setAdmitLoading] = useState(false);
    const [meetingProvider, setMeetingProvider] = useState<TServerMeetingRoomUser>();
    const [userName, setUserName] = useState<string | undefined>();
    const [secondPasted, setSecondPasted] = useState<number>();
    const [hasCloseNextAppointmentTips, setHasCloseNextAppointmentTips] = useState(false);
    const [showSendMessageModal, setShowSendMessageModal] = useState(false);
    const [showSignAndSendModal, setShowSignAndSendModal] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [comingAppointmentDetail, loadingComingAppointmentDetail, fetchComingAppointmentDetail] =
        useGetCommingAppointment(meetingInfo?.appointmentDetail?.id);
    const [showComingAppointmentTips, setShowComingAppointmentTips] = useState(false);
    const [comingAppointmentStarted, setComingAppointmentStarted] = useState(false);
    const [timeDisplay, setTimeDisplay] = useState<string>('');
    const [meetingStartTime, setMeetingStartTime] = useState<string | undefined>(); //"2025-12-17T06:32:52Z"
    const countdownRef = React.useRef<NodeJS.Timeout | null>(null);
    const [showJoinNextConfirm, setShowJoinNextConfirm] = useState(false);
    const [showConfirmEndMeeting, setShowConfirmEndMeeting] = useState(false);
    const [isEnding, setIsEnding] = useState(false);
    const [endFlag, setEndFlag] = useState<{}>();
    const newMessageAccountRef = React.useRef(0);
    const [newMessage, setNewMessage] = useState<{ name: string; value: string } | undefined>();
    const [clientWidth, setClientWidth] = useState<number>(0);
    const [hasTreatmentPlan, setHasTreatmentPlan] = useState(false);
    const onlyOneUserLeftMarkTime = useRef<number | undefined>();
    const [syncAudioFlag, setSyncAudioFlag] = useState<{}>();
    const [startCallFlag, setStartCallFlag] = useState(false);
    const [showPermissionNotAllowModal, setShowPermissionNotAllowModal] = useState(false);
    const markHasPatient = useRef<boolean>(false);
    const prescriptionsModuleHide = hidePrescriptions && hideTreatmentPlan && hideNotes;
    //http://localhost:8085/meeting?sessionkey=dev_mrktjxptjloz&uuid=PROVIDER_1446_ba5f6b7725cb449e9cff74ea2b4bb57e&video=1&audio=1&backgroundItemIndex=1
    const [audioDefaultOn, setAudioDefaultOn] = useState(false);
    const [videoDefaultOn, setVideoDefaultOn] = useState(false);
    const [showInfoConfirmModal, setShowInfoConfirmModal] = useState(false); //get click event
    const prescriptionsRef = useRef<{ getValues: () => any }>(null);
    const [isSaving, setIsSaving] = useState(false);
    //all module showed
    const moveChatBelowVideo = !prescriptionsModuleHide && !hideClientInfo && !hideMessage;
    const floatInfo = React.useRef({
        top: 100,
        width: 160,
        height: 160 * ratio,
        right: 50,
    });
    // 本页是布局外的独立路由，ProviderStore 等基础数据不会由 Layout 拉取，
    // 需自行加载（Sign and send 吊起的 VideoVisitUnSubmittedDrawer 签名弹窗等依赖 provider 信息）
    const [loadBasicData] = useLoadBasicData();

    React.useEffect(() => {
        loadBasicData();
    }, [loadBasicData]);

    React.useEffect(() => {
        if (meetingProvider) {
            setUserName(
                meetingProvider?.userCustomerFullName
                    ? meetingProvider.userCustomerFullName
                    : `${meetingProvider?.firstName} ${meetingProvider?.lastName}`,
            );
        }
    }, [meetingProvider]);
    const appointmentDetail = meetingInfo?.appointmentDetail;

    let targetTimezone:
        | undefined
        | {
              dictKey: string;
              offset: string;
          };
    const appointmentId = meetingInfo?.appointmentDetail?.id;

    const [treatmentAppointmentDetail, loadingTreatmentAppointmentDetail, fetchTreatmentAppointmentDetail] =
        useTreatmentAppointmentDetail(appointmentId);
    //not same as browser timezone
    if (myTimezone?.value) {
        targetTimezone = SUPPORT_TIME_ZONE.find(
            (item) => item.dictKey.toLowerCase() === (myTimezone?.value as string).toLowerCase(),
        );
    }
    const timezone = isDifferentTimezoneWithBrowser ? targetTimezone?.dictKey : undefined;
    let appointmentViewTimeLabel = '';
    let appointmentViewDateLabel = '';
    if (appointmentDetail) {
        appointmentViewTimeLabel = `${dayjs(appointmentDetail.startAt).format(
            dayjs(appointmentDetail.startAt)?.hour() === 0 ? 'HH:mm' : 'hh:mm',
        )} - ${dayjs(appointmentDetail.endAt).format('hh:mm A')}`;
        appointmentViewDateLabel = getTimeStr(dayjs(appointmentDetail?.startAt), 'dddd, MMMM D, YYYY');
        if (timezone) {
            appointmentViewTimeLabel = `${dayjs(appointmentDetail.startAt)
                .tz(timezone)
                .format(dayjs(appointmentDetail.startAt).tz(timezone)?.hour() === 0 ? 'HH:mm' : 'hh:mm')} - ${dayjs(
                appointmentDetail.endAt,
            )
                .tz(timezone)
                .format('hh:mm A')}`;
            appointmentViewDateLabel = getTimeStr(dayjs(appointmentDetail?.startAt).tz(timezone), 'dddd, MMMM D, YYYY');
        }
    }

    React.useEffect(() => {}, [meetingInfo]);

    React.useEffect(() => {
        if (prescriptionsModuleHide && layout === ELayoutType.FLOAT_VIDEO) {
            setLayout(ELayoutType.MAIN_RIGHT);
        }
        if ([ELayoutType.MAIN_LEFT, ELayoutType.MAIN_RIGHT, ELayoutType.CENTER].includes(layout)) {
            setHideClientInfo(true);
        }
    }, [prescriptionsModuleHide, layout]);

    React.useEffect(() => {
        if (checked) {
            if (allowAudio === false || allowVideo === false) {
                setShowPermissionNotAllowModal(true);
            }
        }
    }, [checked, allowAudio, allowVideo]);

    React.useEffect(() => {
        const video = getUrlParam('video');
        if (video === '1') {
            setVideoDefaultOn(true);
        }
        const audio = getUrlParam('audio');
        if (audio === '1') {
            setAudioDefaultOn(true);
        }
        setShowInfoConfirmModal(true);
    }, []);

    React.useEffect(() => {
        if (comingAppointmentDetail) {
            const startAt = dayjs(comingAppointmentDetail.startAt);
            const now = dayjs();
            const diff = startAt.diff(now, 'seconds');
            // setNextMeetingLeftMinutes(3);
            // setShowComingAppointmentTips(true);
            // setComingAppointmentStarted(true);
            // return;
            setTimeout(() => {
                setComingAppointmentStarted(true);
            }, diff * 1000);
            //5min
            if (diff < 60 * 5) {
                let minLeft = Math.floor(diff / 60);
                if (minLeft <= 1) {
                    minLeft = 1;
                }
                setNextMeetingLeftMinutes(minLeft);
                setShowComingAppointmentTips(true);
                return;
            }
            if (diff >= 60 * 5) {
                setTimeout(
                    () => {
                        setShowComingAppointmentTips(true);
                    },
                    (diff - 60 * 5) * 1000,
                );
            }
        }
    }, [comingAppointmentDetail]);

    React.useEffect(() => {
        // 以「最近一次登录」写入的共享 cookie 为准，同步 schedule-token，
        // 避免从 meeting.* 子域跳转过来时本地 token 与开会所用 token 不一致导致接口报错。
        syncScheduleTokenFromCookie();

        const sessionkey = getUrlParam('sessionkey');
        const exsitUuid = getMeetingUuid();
        const uuid = getUrlParam('uuid') || exsitUuid;
        setClientWidth(window.innerWidth);

        if (sessionkey && uuid) {
            setSessionKey(sessionkey);
            setMeetingUuid(uuid);
        }
    }, []);

    React.useEffect(() => {
        if (meetingStartTime) {
            countdownRef.current = setInterval(() => {
                const now = dayjs();
                const startTime = dayjs(meetingStartTime);
                const diff = now.diff(startTime, 'seconds');
                if (diff > 0) {
                    //format to 00:00:00
                    const hours = Math.floor(diff / 3600);
                    const minutes = Math.floor((diff % 3600) / 60);
                    const seconds = diff % 60;
                    if (hours > 0) {
                        setTimeDisplay(
                            `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
                                .toString()
                                .padStart(2, '0')}`,
                        );
                    } else {
                        setTimeDisplay(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
                    }
                } else {
                    setTimeDisplay('');
                }
            }, 1000);
        }
        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [meetingStartTime]);

    React.useEffect(() => {
        if (layout === ELayoutType.FLOAT_VIDEO) {
            const videoWrapProvider = document.getElementById('video-wrap-provider');
        }
    }, [layout]);

    React.useEffect(() => {
        if (meetingInfo?.appointmentDetail) {
            setPatientId(meetingInfo?.appointmentDetail.clientId);
            patientIdRef.current = meetingInfo?.appointmentDetail.clientId;
        }
        if (meetingInfo?.meetingRoomUsers) {
            const _provider = meetingInfo?.meetingRoomUsers.find(
                (user) => user.userType === EMeetingUserType.PROVIDER && user.isVideoRoomOnline,
            );
            if (_provider) {
                setMeetingProvider(_provider);
                setMeetingStartTime(_provider.videoRoomFirstJoinAt);
            }
        }
        const waiting = meetingInfo?.meetingRoomUsers.find(
            (user) =>
                user.isWaitingOnline &&
                user.userType === EMeetingUserType.KLARITY_USER &&
                !user.adminFlag &&
                !admitedUserIds.current.includes(user.userId),
        );
        const patient = meetingInfo?.meetingRoomUsers.find((user) => user.userType === EMeetingUserType.KLARITY_USER);
        if (patient) {
            markHasPatient.current = true;
        } else if (markHasPatient.current) {
            message.info('The patient has left the visit.');
            markHasPatient.current = false;
        }
        if (!waiting) {
            setPendingJoinUser(undefined);
        }
        if (waiting && !pendingJoinUser) {
            setRefreshTime(ZOOM_AUTO_REFRESH_INTERVAL);
            setPendingJoinUser(waiting);
        }
    }, [meetingInfo, pendingJoinUser]);

    const clientInfoWrapClass = s.module;
    let prescriptionsWrapClass = `${s.module} ${s.moduleFlow}`;
    let videoWrapClass = `${s.module} ${s.moduleVideoSmall}`;
    let messageWrapClass = `${s.module} ${s.moduleMessage}`;

    const oneThirdClientWidth = clientWidth / 3;

    let videoWidth = oneThirdClientWidth || 480;
    let videoHeight = videoWidth * ratio;

    let zoomLayout = 'picture-in-picture';

    if (hideMessage) {
        messageWrapClass = `${s.module} ${s.moduleMessage} ${s.moduleMessageHide}`;
    }

    if (layout === ELayoutType.MAIN_RIGHT) {
        if (!hideClientInfo || !hideMessage) {
            videoWidth = oneThirdClientWidth || 480;
            videoHeight = videoWidth * ratio;
            videoWrapClass = `${s.module} ${s.moduleVideoSmall}`;
        }
        if (hideClientInfo && hideMessage) {
            videoWidth = oneThirdClientWidth * 2 || 900;
            videoHeight = videoWidth * ratio;
            videoWrapClass = `${s.module} ${s.moduleVideoLarge}`;
        }
    }
    if (layout === ELayoutType.CENTER) {
        if (!hideClientInfo || !hideMessage) {
            videoWidth = oneThirdClientWidth || 480;
            videoHeight = videoWidth * ratio;
            videoWrapClass = `${s.module} ${s.moduleVideoSmall}`;
        }
        if (hideClientInfo && hideMessage) {
            try {
                const bodyWidth = window.document.body.scrollWidth;
                videoWidth = bodyWidth / 2;
                videoHeight = videoWidth * 0.5625;
                videoWrapClass = `${s.module} ${s.moduleVideoCenter}`;
            } catch (error) {
                console.error(error);
            }
        }
    }
    if (layout === ELayoutType.FLOAT_VIDEO) {
        videoWrapClass = `${s.module} ${s.moduleVideoFloat}`;
        zoomLayout = 'float';
        videoWidth = floatInfo.current.width;
        videoHeight = floatInfo.current.height;
    }

    if (prescriptionsModuleHide) {
        //make video full
        prescriptionsWrapClass = `${s.module} ${s.moduleFlowHide}`;
        videoWrapClass = `${s.module} ${s.moduleFlow}`;
        const screenWidth = window.innerWidth;
        videoWidth = screenWidth;
        if (!hideMessage) {
            videoWidth -= 380;
        }
        if (!hideClientInfo) {
            videoWidth -= 360;
        }
        videoHeight = videoWidth * ratio;
    }

    if (moveChatBelowVideo) {
        messageWrapClass = `${s.module} ${s.moduleMessage} ${s.moduleMessageHide}`;
        videoWrapClass = `${s.module} ${s.moduleVideoMiddle}`;
        videoWidth = 500;
        videoHeight = videoWidth * ratio;
    }

    const handleLayoutChange = (_layout: ELayoutType) => {
        setPopoverOpen(false);
        setTimeout(() => {
            setLayout(_layout);
        }, 100);
    };

    const handleVideoWrapClick = () => {
        if (layout === ELayoutType.FLOAT_VIDEO) {
            setLayout(ELayoutType.CENTER);
        }
    };

    const handleAdmitMeeting = async (user: TServerMeetingRoomUser) => {
        setAdmitLoading(true);
        try {
            const uuid = getMeetingUuid();
            if (!uuid || !sessionKey) return;
            const result = await meetingApprove(sessionKey!, getMeetingUuid()!, user.userId);
            if (!result.error) {
                message.success('Patient admitted successfully.');
                admitedUserIds.current.push(user.userId);
                setTimeout(() => {
                    admitedUserIds.current = [];
                }, 10000);
                setPendingJoinUser(undefined);
            }
        } catch (error) {
            console.error(error);
        }
        setAdmitLoading(false);
    };

    const handleClickSendQuickMessage = async () => {
        setShowSendMessageModal(true);
    };

    const handleClickJoinNext = async () => {
        setShowJoinNextConfirm(true);
    };

    const handleJoinNextConfirm = async () => {
        setShowJoinNextConfirm(false);
        window.open(comingAppointmentDetail?.externalVideoLink, '_self');
    };

    const onActionZoomCallAction = (actionType: EZoomVideoViewActionType) => {
        if (actionType === EZoomVideoViewActionType.MESSAGE) {
            newMessageAccountRef.current = 0;
            setNewMessage(undefined);
            setHideMessage(false);
        }
        if (actionType === EZoomVideoViewActionType.END_CALL) {
            setShowConfirmEndMeeting(true);
        }
        if (actionType === EZoomVideoViewActionType.CALL_IS_ENDED) {
            navigate(
                `/${PATH.MEETING_END}?hasTreatmentPlan=${hasTreatmentPlan}&clientId=${patientIdRef.current}&appointmentId=${appointmentId ?? ''}`,
            );
        }
    };

    const onNewMessage = (name: string, value: TMessage) => {
        if (!hideMessage) {
            return;
        }
        const senderId = value?.senderId;
        if (senderId === meetingProvider?.userId) {
            return;
        }
        let content = '';
        if (value?.messageType === EMessageType.TEXT) {
            content = value.text?.body || '';
        }
        if (value?.messageType === EMessageType.FILE) {
            content = value.file?.fileType.startsWith('image/') ? 'picture' : 'pdf';
        }
        if (!content) return;
        if (!newMessage) {
            newMessageAccountRef.current = 1;
            setNewMessage({ name, value: value.text?.body || '' });
        } else {
            if (newMessage.name === name && newMessage.value === value.text?.body) {
                return;
            }
            newMessageAccountRef.current += 1;
            setNewMessage({ name, value: value.text?.body || '' });
        }
    };
    const handleDoEndMeeting = async () => {
        setIsEnding(true);
        const res = await meetingEnd(sessionKey!);
        if (!res.error) {
            const hasTextVisitTreatment = !!res?.data?.data?.hasTextVisitTreatment;
            setHasTreatmentPlan(hasTextVisitTreatment);
            setShowConfirmEndMeeting(false);
            setEndFlag({});
        }
        setIsEnding(false);
    };

    const handleSubmitOrSave = async (isSave: boolean): Promise<boolean> => {
        if (!treatmentAppointmentDetail || !appointmentId || isSaving) {
            return false;
        }
        try {
            setIsSaving(true);
            const params = prescriptionsRef.current?.getValues();
            if (!params?.treatmentPlan) {
                message.error('Please add visit notes');
                setIsSaving(false);
                return false;
            }
            const res = await submitVideoTreatment({
                ...params,
                attachments:
                    params.attachments?.map((file: TextVisitAttachment) => ({
                        name: file.name,
                        path: file.path,
                        type: file.type,
                    })) || [],
                businessType: EBusinessType.APPOINTMENT,
                businessId: appointmentId,
                submit: !isSave,
            });
            if (!res.error) {
                message.success(isSave ? 'Draft saved successfully.' : 'Visit Notes sent successfully.');
                fetchTreatmentAppointmentDetail(appointmentId);
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            message.error('Failed to save visit notes. Please try again.');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    React.useEffect(() => {
        //check is end
        //people least than two and meeting end over 10 min
        const _handleDoEndMeeting = async () => {
            setIsEnding(true);
            const res = await meetingEnd(sessionKey!);
            if (!res.error) {
                setShowConfirmEndMeeting(false);
                setEndFlag({});
            }
            setIsEnding(false);
        };
        if (meetingInfo?.appointmentDetail?.endAt) {
            const endTime = dayjs(meetingInfo?.appointmentDetail?.endAt).valueOf();
            const nowTime = dayjs().valueOf();
            const pastEndTime = nowTime - endTime > 0;
            const roomUsers = meetingInfo?.meetingRoomUsers || [];
            if (roomUsers.length <= 1 && pastEndTime) {
                if (!onlyOneUserLeftMarkTime.current) {
                    onlyOneUserLeftMarkTime.current = nowTime;
                }
            } else {
                onlyOneUserLeftMarkTime.current = undefined;
            }
            if (onlyOneUserLeftMarkTime.current) {
                if (nowTime - onlyOneUserLeftMarkTime.current > 10 * 60 * 1000) {
                    _handleDoEndMeeting();
                }
            }
        }
    }, [sessionKey, meetingInfo]);

    return (
        <div className={s.wrap}>
            <div className={s.nav}>
                <img src={klarityLogo} className={s.logo} />
                <div className={s.navRight}>
                    <div
                        onClick={() => {
                            setHideClientInfo(false);
                        }}
                        className={s.navItem}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M10.6668 4.66667C10.6668 6.13943 9.47292 7.33333 8.00016 7.33333C6.5274 7.33333 5.3335 6.13943 5.3335 4.66667C5.3335 3.19391 6.5274 2 8.00016 2C9.47292 2 10.6668 3.19391 10.6668 4.66667Z"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M8.00016 9.33333C5.42283 9.33333 3.3335 11.4227 3.3335 14H12.6668C12.6668 11.4227 10.5775 9.33333 8.00016 9.33333Z"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Patient profile
                    </div>
                    <Popover
                        open={popoverOpen}
                        onOpenChange={setPopoverOpen}
                        trigger="hover"
                        placement="bottomRight"
                        content={
                            <div className={s.layoutPop}>
                                <div className={s.imgWrap}>
                                    <div
                                        onClick={() => {
                                            handleLayoutChange(ELayoutType.MAIN_LEFT);
                                        }}
                                        className={`${s.img} ${layout === ELayoutType.MAIN_LEFT ? s.imgSelect : ''}`}
                                    >
                                        <svg
                                            width="54"
                                            height="54"
                                            viewBox="0 0 54 54"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect width="38.51" height="16.2927" fill="currentColor" />
                                            <rect y="18.5146" width="38.51" height="16.2927" fill="currentColor" />
                                            <rect y="37.0283" width="38.51" height="16.2927" fill="currentColor" />
                                            <rect x="39.9912" width="13.3304" height="53.3215" fill="currentColor" />
                                        </svg>
                                    </div>
                                    <div
                                        onClick={() => {
                                            handleLayoutChange(ELayoutType.MAIN_RIGHT);
                                        }}
                                        className={`${s.img} ${layout === ELayoutType.MAIN_RIGHT ? s.imgSelect : ''}`}
                                    >
                                        <svg
                                            width="54"
                                            height="54"
                                            viewBox="0 0 54 54"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect width="13.49" height="16.2927" fill="currentColor" />
                                            <rect y="18.5146" width="13.49" height="16.2927" fill="currentColor" />
                                            <rect y="37.0283" width="13.49" height="16.2927" fill="currentColor" />
                                            <rect x="15.4902" width="38.51" height="53.3215" fill="currentColor" />
                                        </svg>
                                    </div>
                                    <div
                                        onClick={() => {
                                            handleLayoutChange(ELayoutType.CENTER);
                                        }}
                                        className={`${s.img} ${layout === ELayoutType.CENTER ? s.imgSelect : ''}`}
                                    >
                                        <svg
                                            width="54"
                                            height="54"
                                            viewBox="0 0 54 54"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect width="25.9206" height="16.2929" fill="currentColor" />
                                            <rect y="18.5146" width="25.9206" height="16.2929" fill="currentColor" />
                                            <rect y="37.0283" width="25.9206" height="16.2929" fill="currentColor" />
                                            <rect x="28.1416" width="25.1796" height="53.3215" fill="currentColor" />
                                        </svg>
                                    </div>
                                    <div
                                        onClick={() => {
                                            if (prescriptionsModuleHide) return;
                                            handleLayoutChange(ELayoutType.FLOAT_VIDEO);
                                        }}
                                        className={`${s.img} ${layout === ELayoutType.FLOAT_VIDEO ? s.imgSelect : ''} ${
                                            prescriptionsModuleHide ? s.imgDisabled : ''
                                        }`}
                                    >
                                        <svg
                                            width="54"
                                            height="54"
                                            viewBox="0 0 54 54"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect width="53.3215" height="53.3215" fill="currentColor" />
                                            <rect
                                                x="24.4395"
                                                y="3.70312"
                                                width="25.9206"
                                                height="16.2929"
                                                fill="white"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className={s.cList}>
                                    <div className={s.cItem}>
                                        <p>Hide Prescriptions</p>
                                        <Switch
                                            checked={hidePrescriptions}
                                            onChange={(checked) => {
                                                setHidePrescriptions(checked);
                                            }}
                                        />
                                    </div>
                                    <div className={s.cItem}>
                                        <p>Hide Treatment Plan</p>
                                        <Switch
                                            checked={hideTreatmentPlan}
                                            onChange={(checked) => {
                                                setHideTreatmentPlan(checked);
                                            }}
                                        />
                                    </div>
                                    <div className={s.cItem}>
                                        <p>Hide Notes</p>
                                        <Switch
                                            checked={hideNotes}
                                            onChange={(checked) => {
                                                setHideNotes(checked);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <div className={s.navItem}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M2.6665 3.33366C2.6665 2.96547 2.96498 2.66699 3.33317 2.66699H12.6665C13.0347 2.66699 13.3332 2.96547 13.3332 3.33366V4.66699C13.3332 5.03518 13.0347 5.33366 12.6665 5.33366H3.33317C2.96498 5.33366 2.6665 5.03518 2.6665 4.66699V3.33366Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M2.6665 8.66699C2.6665 8.2988 2.96498 8.00033 3.33317 8.00033H7.33317C7.70136 8.00033 7.99984 8.2988 7.99984 8.66699V12.667C7.99984 13.0352 7.70136 13.3337 7.33317 13.3337H3.33317C2.96498 13.3337 2.6665 13.0352 2.6665 12.667V8.66699Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M10.6665 8.66699C10.6665 8.2988 10.965 8.00033 11.3332 8.00033H12.6665C13.0347 8.00033 13.3332 8.2988 13.3332 8.66699V12.667C13.3332 13.0352 13.0347 13.3337 12.6665 13.3337H11.3332C10.965 13.3337 10.6665 13.0352 10.6665 12.667V8.66699Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Layout
                        </div>
                    </Popover>
                    <div className={s.navTime}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M8 5.33333V8L10 10M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                                stroke="#1F2937"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {timeDisplay}
                    </div>
                </div>
            </div>
            {showComingAppointmentTips && !hasCloseNextAppointmentTips && (
                <div className={`${s.nextTips} ${comingAppointmentStarted ? s.nextTipsStarted : ''}`}>
                    {comingAppointmentStarted && (
                        <div className={s.left}>⏰ Your next patient is waiting in the video room.</div>
                    )}
                    {!comingAppointmentStarted && (
                        <div className={s.left}>⏰ Your next visit starts in {nextMeetingLeftMinutes} minutes.</div>
                    )}

                    <div className={s.right}>
                        <div className={s.rBtn} onClick={handleClickSendQuickMessage}>
                            Send quick message
                        </div>
                        {comingAppointmentDetail?.externalVideoLink && (
                            <div className={s.rBtn} onClick={handleClickJoinNext}>
                                {comingAppointmentStarted ? 'Join now' : 'Join next appointment'}
                            </div>
                        )}

                        <div
                            className={s.close}
                            onClick={() => {
                                setHasCloseNextAppointmentTips(true);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M6 18L18 6M6 6L18 18"
                                    stroke="#111827"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
            <div className={`${s.content} ${showComingAppointmentTips ? s.contentComingAppointmentTips : ''}`}>
                {!hideClientInfo && (
                    <div className={clientInfoWrapClass}>
                        <div className={s.clientInfo}>
                            <ProsepectInfo
                                billingPolicy={billingPolicy}
                                connectAccountInfo={connectAccountInfo}
                                hideCharge
                                smallSize
                                data={loadingTargetClientDetail ? undefined : targetClientDetail}
                                onUpdate={() => {
                                    runTargetClientDetail();
                                }}
                            />
                            <span
                                onClick={() => {
                                    setHideClientInfo(true);
                                }}
                                className={s.close}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                >
                                    <path
                                        d="M1 13L13 1M1 1L13 13"
                                        stroke="#111827"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </div>
                    </div>
                )}

                <div className={prescriptionsWrapClass}>
                    <div className={s.prescriptions}>
                        <Prescriptions
                            ref={prescriptionsRef}
                            caseData={treatmentAppointmentDetail}
                            businessId={appointmentId!}
                            refetch={(_appointmentId) => fetchTreatmentAppointmentDetail(_appointmentId as number)}
                            type="video"
                            hidePrescriptions={hidePrescriptions}
                            hideTreatmentPlan={hideTreatmentPlan}
                            hideNotes={hideNotes}
                            onSave={() => handleSubmitOrSave(true)}
                            onSignAndSend={() => {
                                setShowSignAndSendModal(true);
                            }}
                            saving={isSaving}
                            visitNotesSubmitted={!!treatmentAppointmentDetail?.providerTreatmentSubmitted}
                            // 页面正常显示后才触发模板引导：白屏 loading 结束 + Confirm visit details 弹窗已关闭。
                            // 不依赖 Zoom join 成功——join 慢或失败时页面照常可用，引导也应能出现
                            enableTemplateGuide={appLoadingDone && startCallFlag}
                        />
                    </div>
                </div>
                <div
                    className={videoWrapClass}
                    id="video-wrap-provider"
                    style={
                        layout === ELayoutType.FLOAT_VIDEO
                            ? {
                                  top: `${floatInfo.current.top}px`,
                                  right: `${floatInfo.current.right}px`,
                                  width: `${floatInfo.current.width}px`,
                                  height: `${floatInfo.current.height}px`,
                              }
                            : undefined
                    }
                >
                    <div className={s.video} onClick={handleVideoWrapClick}>
                        <ProviderZoomCallView
                            pipVideoWidth={160}
                            pipVideoHeight={90}
                            mode="call"
                            videoDefaultOn={videoDefaultOn}
                            audioDefaultOn={audioDefaultOn}
                            videoWidth={videoWidth}
                            videoHeight={videoHeight}
                            userName={userName}
                            autoShowVideo
                            sessionKey={LOCAL_TEST ? testInfo?.sessionName : sessionKey}
                            serverMeetingInfo={meetingInfo}
                            client={targetClientDetail}
                            token={LOCAL_TEST ? testInfo?.hostToken : zoomToken}
                            onAction={onActionZoomCallAction}
                            onMeetingInfoUpdated={() => {
                                fetchMeetingInfo();
                            }}
                            layout={zoomLayout as 'default' | 'picture-in-picture' | 'float'}
                            endFlag={endFlag}
                            newCommingMessage={newMessage}
                            newCommingMessageAmount={newMessageAccountRef.current}
                            ratio={ratio}
                            startFlag={startCallFlag}
                        />
                    </div>
                    {moveChatBelowVideo && (
                        <div className={s.messageBelowVideo}>
                            <ChatBox
                                patientId={patientId!}
                                onClose={() => {
                                    setHideMessage(true);
                                }}
                                mode="FILE"
                                onNewMessage={onNewMessage}
                            />
                        </div>
                    )}
                </div>
                <div className={messageWrapClass}>
                    <ChatBox
                        patientId={patientId!}
                        onClose={() => {
                            setHideMessage(true);
                        }}
                        mode="FILE"
                        onNewMessage={onNewMessage}
                        fixTop
                        fixBottom
                    />
                </div>
            </div>
            <div className={`${s.pendingJoin} ${pendingJoinUser ? s.pendingJoinShow : ''}`}>
                <img src={IconLogo} alt="iconLogo" />
                <p>
                    {pendingJoinUser?.firstName} {pendingJoinUser?.lastName} wants to join
                </p>
                <Button
                    type="primary"
                    size="large"
                    className={s.btn}
                    loading={admitLoading}
                    onClick={() => {
                        handleAdmitMeeting(pendingJoinUser!);
                        setSyncAudioFlag({});
                    }}
                >
                    Admit
                </Button>
            </div>
            <SendMessageToPatientModal
                appointmentDetail={comingAppointmentDetail}
                open={showSendMessageModal}
                onClose={() => {
                    setShowSendMessageModal(false);
                }}
                onSuccess={() => {
                    setShowSendMessageModal(false);
                    setHasCloseNextAppointmentTips(true);
                }}
            />
            {/* Sign and send：与 VideoVisitUnSubmittedDrawer 的 Submit 确认弹窗保持一致 */}
            <Modal
                open={showSignAndSendModal}
                onCancel={() => setShowSignAndSendModal(false)}
                okText="Sign & Send"
                okButtonProps={{ loading: isSaving }}
                onOk={async () => {
                    if (!hasSignature) {
                        message.warning('Please add your signature before submitting.');
                        return;
                    }
                    const success = await handleSubmitOrSave(false);
                    if (success) {
                        setShowSignAndSendModal(false);
                    }
                }}
                width={512}
            >
                <div className="flex gap-4">
                    <div className={s.submitModalIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M8.68387 13.3419C8.88616 12.9381 9 12.4824 9 12C9 11.5176 8.88616 11.0619 8.68387 10.6581M8.68387 13.3419C8.19134 14.3251 7.17449 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.17449 9 8.19134 9.67492 8.68387 10.6581M8.68387 13.3419L15.3161 16.6581M8.68387 10.6581L15.3161 7.34193M15.3161 7.34193C15.8087 8.32508 16.8255 9 18 9C19.6569 9 21 7.65685 21 6C21 4.34315 19.6569 3 18 3C16.3431 3 15 4.34315 15 6C15 6.48237 15.1138 6.93815 15.3161 7.34193ZM15.3161 16.6581C15.1138 17.0619 15 17.5176 15 18C15 19.6569 16.3431 21 18 21C19.6569 21 21 19.6569 21 18C21 16.3431 19.6569 15 18 15C16.8255 15 15.8087 15.6749 15.3161 16.6581Z"
                                stroke="#0284C7"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="text-gray-900 text-lg font-medium">Submit visit notes to patient</div>
                        <div className="text-gray-500 text-sm">
                            Please review before submitting. After submission, the visit notes will be{' '}
                            <span className="font-bold">signed and sent to the patient immediately</span>. It will also
                            be recorded under your name with a timestamp.
                        </div>
                    </div>
                </div>
                <ProviderSignature onSignatureStatusChange={setHasSignature} />
            </Modal>
            <Modal
                open={showJoinNextConfirm}
                title="Join next visit"
                onCancel={() => {
                    setShowJoinNextConfirm(false);
                }}
                okButtonProps={{
                    danger: true,
                    onClick: handleJoinNextConfirm,
                }}
                okText="End & join next"
            >
                <div className={s.content}>
                    Joining the next visit will automatically close your current visit. Please confirm before switching
                    to the next visit.
                </div>
            </Modal>

            <Modal
                open={showConfirmEndMeeting}
                title="End meeting"
                onCancel={() => {
                    setShowConfirmEndMeeting(false);
                }}
                okButtonProps={{
                    danger: true,
                    onClick: handleDoEndMeeting,
                    loading: isEnding,
                }}
                okText="End visit for all"
            >
                <div className={s.content}>
                    Are you sure you want to end this visit? The session will be closed for both you and the patient.
                </div>
            </Modal>
            <Modal
                open={showInfoConfirmModal}
                title="Confirm visit details"
                onCancel={() => {
                    setStartCallFlag(true);
                    setShowInfoConfirmModal(false);
                }}
                onOk={() => {
                    setStartCallFlag(true);
                    setShowInfoConfirmModal(false);
                }}
                cancelText="Skip"
                okText="Enter now"
                width={540}
            >
                <div className={s.confirmContent}>
                    <div className={s.cTitle}>Please confirm the details below to enter the visit.</div>
                    {!meetingInfo && (
                        <div className={s.cLoading}>
                            <Spin />
                        </div>
                    )}
                    {meetingInfo && (
                        <div className={s.cContent}>
                            <div className={s.cItem}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                                        stroke="#6B7280"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
                                        stroke="#6B7280"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {targetClientDetail?.firstName} {targetClientDetail?.lastName}
                            </div>
                            <div className={s.cItem}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                        stroke="#6B7280"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {appointmentViewDateLabel}
                            </div>
                            <div className={s.cItem}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                        stroke="#6B7280"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {appointmentViewTimeLabel} {myTimezone?.value}
                                {' | '}
                                {appointmentDetail?.duration}
                                {' mins'}
                            </div>
                            <div className={s.cItem}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M21 13.2554C18.2207 14.3805 15.1827 15 12 15C8.8173 15 5.7793 14.3805 3 13.2554M16 6V4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4V6M12 12H12.01M5 20H19C20.1046 20 21 19.1046 21 18V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V18C3 19.1046 3.89543 20 5 20Z"
                                        stroke="#6B7280"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {meetingInfo?.appointmentDetail?.serviceType}
                            </div>
                            <div className={s.cItem}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21M19 21L21 21M19 21H14M5 21L3 21M5 21H10M9 6.99998H10M9 11H10M14 6.99998H15M14 11H15M10 21V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V21M10 21H14"
                                        stroke="#6B7280"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {meetingInfo?.appointmentDetail?.serviceSpecialty}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
            {/* <Modal
                title="Meet start"
                open
                footer={(
                    <Button className={s.btn} type="primary" size="large">
                        Ok
                    </Button>
                )}
            >
                <div className={s.content}>
                    You meeting with Peter has started.
                    <Button block type="default" size="large" className={s.btn} onClick={handleClickJoinNext}>
                        <div className={s.btnText}>Connect with audio and video</div>
                    </Button>
                    <Button block type="default" size="large" className={s.btn} onClick={handleClickJoinNext}>
                        <div className={s.btnText}>Connect with audio only</div>
                    </Button>
                    <Button block type="default" size="large" className={s.btn} onClick={handleClickJoinNext}>
                        <div className={s.btnText}>Connect with video only</div>
                    </Button>
                    <Button block type="default" size="large" className={s.btn} onClick={handleClickJoinNext}>
                        <div className={s.btnText}>Connect without video and audio</div>
                    </Button>
                </div>

            </Modal> */}
            <PermissionNotAllowModal
                show={showPermissionNotAllowModal}
                onCancel={() => {
                    setShowPermissionNotAllowModal(false);
                }}
                onOk={() => {
                    setShowPermissionNotAllowModal(false);
                }}
            />
        </div>
    );
};

export default MeetingPage;
